/**
 * Pestaña 1 — Novedades TMS → Visma Payroll.
 *
 * El origen es el TMS: se elige la operación y el período de liquidación, el
 * portal consulta la base de esa operación y consolida ese trabajo según las
 * reglas de RRHH. El período entra entero y se corta por la fecha de **inicio**
 * del tramo, así que un viaje que arranca el último día del mes entra aunque
 * termine en el siguiente.
 *
 * Consultar y previsualizar no modifican nada. Lo único que escribe es el
 * impacto en Visma, que pide confirmación y exige rol de Liquidador.
 *
 * La identidad del colaborador es el **DNI**. El legajo se muestra como dato y
 * sirve de filtro del lado del servidor: los tramos de fleteros no entran.
 *
 * El recorrido es consulta → conceptos → previsualización, y los dos últimos
 * van lado a lado: destildar un concepto tiene que mostrar su efecto en el
 * mismo momento, no después de bajar la pantalla.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Download,
  Loader2,
  Search,
  Send,
} from 'lucide-react';

import {
  mensajeDe,
  type RespuestaConsulta,
  type RespuestaImpacto,
  type TipoChofer,
  type TramoTms,
} from '../../api';
import { Avisos } from '../../components/Aviso';
import { CampoFecha } from '../../components/CampoFecha';
import { mensaje, type Mensaje } from '../../components/mensajes';
import { ModalCargando } from '../../components/ModalCargando';
import { Paginacion } from '../../components/Paginacion';
import { EnvoltorioTabla } from '../../components/Tabla';
import { useSesion } from '../../components/useSesion';
import { esperarDescarga, tokenDeDescarga, type EsperaDeDescarga } from '../../lib/descargas';
import { mesYAnio, soloFecha } from '../../lib/fechas';
import { paginaValida } from '../../lib/paginacion';
import {
  consultar,
  impactarEnVisma,
  operaciones as pedirOperaciones,
  simularImpacto,
  urlConsultaXlsx,
  type SeleccionPeriodo,
} from './api';
import { DialogoImpactar, type ResultadoIntento } from './DialogoImpactar';
import {
  conceptosDelMes,
  conceptosManuales,
  problemaDelRango,
  sePuedeTildar,
  textoDeRango,
} from './rango';
import {
  alternarChofer,
  esMesVencido,
  estaDesactualizado,
  limitesDelMes,
  moverMes,
  ORDEN_CHOFERES,
  selloDe,
} from './banda';
import {
  nombreDeLosTipos,
  seleccionConsultada,
  seleccionDe,
  textoDeLoConsultado,
} from './periodo';

/**
 * Registros por página. Un mes de una operación grande trae miles de tramos:
 * de a treinta se leen, y el navegador no se traba dibujándolos todos.
 */
const POR_PAGINA = 30;

/**
 * La previsualización va de a quince.
 *
 * Es la tabla que se lee fila por fila antes de liquidar —quién cobra qué— y
 * comparte el alto con los conceptos que están al lado: treinta obligaban a
 * scrollear la página para volver a destildar algo.
 */
const POR_PAGINA_NOVEDADES = 10;

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
] as const;

/** Las novedades se cargan a mes vencido: el default es el mes anterior. */
function periodoPorDefecto(): { anio: number; mes: number } {
  const hoy = new Date();
  // Día 1 a propósito: con el día de hoy, el 31/03 pasaba a "31/02" y
  // JavaScript lo corría al 03/03 — el default proponía marzo.
  const anterior = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
  return { anio: anterior.getFullYear(), mes: anterior.getMonth() + 1 };
}

/** "julio 2026", para los textos de la pantalla. */
const periodoLegible = (anio: number, mes: number): string =>
  `${(MESES[mes - 1] ?? '').toLowerCase()} ${String(anio)}`;

/** "01150, 01154 y 01188": una lista como se lee, no pegada con "y". */
function enumerar(partes: readonly string[]): string {
  if (partes.length <= 1) return partes[0] ?? '';
  return `${partes.slice(0, -1).join(', ')} y ${partes[partes.length - 1] ?? ''}`;
}

const numero = (valor: number): string =>
  valor === 0 ? '—' : valor.toLocaleString('es-AR', { maximumFractionDigits: 2 });

type EstadoPaso = 'pendiente' | 'activo' | 'hecho';

export function VistaNovedades(): React.JSX.Element {
  const inicial = periodoPorDefecto();
  const [anio, setAnio] = useState(inicial.anio);
  const [mes, setMes] = useState(inicial.mes);
  const [operacion, setOperacion] = useState('');
  const [disponibles, setDisponibles] = useState<readonly string[]>([]);

  /*
   * De qué choferes se habla. Arranca sin elegir y hasta que se elija no se
   * muestra período: el propio va por mes y el eventual por un intervalo, así
   * que no hay un control que sirva para los dos ni un default razonable.
   */
  const [tipos, setTipos] = useState<ReadonlySet<TipoChofer>>(
    // Las dos por omisión: lo normal es liquidar a todo el mundo.
    () => new Set<TipoChofer>(['PROPIO', 'EVENTUAL']),
  );
  const [desde, setDesde] = useState(() => limitesDelMes(inicial.anio, inicial.mes).primerDia);
  const [hasta, setHasta] = useState(() => limitesDelMes(inicial.anio, inicial.mes).ultimoDia);
  /*
   * Mientras nadie toque el intervalo de los eventuales, sigue al mes del
   * período: cambiar de mes y tener que reescribir las dos fechas es el error
   * que termina liquidando la ventana del mes anterior.
   */
  const [intervaloTocado, setIntervaloTocado] = useState(false);

  const [consulta, setConsulta] = useState<RespuestaConsulta | null>(null);
  /** Hora en que volvió la consulta, para el sello de la banda. */
  const [horaConsulta, setHoraConsulta] = useState('');
  const [avisos, setAvisos] = useState<readonly Mensaje[]>([]);
  const [consultando, setConsultando] = useState(false);
  const [paginaConceptos, setPaginaConceptos] = useState(1);
  const [paginaNovedades, setPaginaNovedades] = useState(1);
  const [paginaTramos, setPaginaTramos] = useState(1);
  const [tramosAbiertos, setTramosAbiertos] = useState(false);
  const [descargando, setDescargando] = useState(false);

  /*
   * Los conceptos que no se liquidan por el mes —preventa y supermercado—
   * salen de un intervalo que se escribe acá. `rangoAplicado` es el que de
   * verdad se consultó: es el que viaja al Excel y al impacto, para que los
   * tres miren las mismas fechas. Lo que está en los dos campos puede estar a
   * medio escribir.
   */
  const [manualDesde, setManualDesde] = useState('');
  const [manualHasta, setManualHasta] = useState('');
  /** Token del acuse de la descarga en curso. Cambia con cada planilla. */
  const [tokenDescarga, setTokenDescarga] = useState(tokenDeDescarga);
  const espera = useRef<EsperaDeDescarga | null>(null);

  /*
   * El impacto en Visma, que es lo único de esta pantalla que escribe en una
   * liquidación. Se abre con un simulacro —el servidor arma el payload exacto
   * sin postear— y recién el botón del diálogo lo manda de verdad.
   */
  const { sesion } = useSesion();
  const puedeImpactar = sesion?.rol === 'admin' || sesion?.rol === 'liquidador';
  const [dialogoAbierto, setDialogoAbierto] = useState(false);
  const [simulacro, setSimulacro] = useState<RespuestaImpacto | null>(null);
  const [preparando, setPreparando] = useState(false);
  const [impactando, setImpactando] = useState(false);
  const [intento, setIntento] = useState<ResultadoIntento | null>(null);
  const [errorImpacto, setErrorImpacto] = useState<string | null>(null);
  /** Conceptos tildados: arrancan todos, porque lo normal es impactar todo. */
  const [elegidos, setElegidos] = useState<ReadonlySet<string>>(new Set());

  // Las operaciones las define el servidor: si no hay conexión configurada al
  // TMS, el desplegable queda vacío y el motivo se muestra como aviso.
  useEffect(() => {
    let vigente = true;
    void (async () => {
      try {
        const lista = await pedirOperaciones();
        if (!vigente) return;
        setDisponibles(lista);
        setOperacion((actual) => (actual === '' ? (lista[0] ?? '') : actual));
      } catch (err) {
        if (vigente) setAvisos([mensaje('error', mensajeDe(err))]);
      }
    })();
    return () => {
      vigente = false;
    };
  }, []);

  // Si la pantalla se va mientras se espera una descarga, el reloj del acuse
  // tiene que irse con ella.
  useEffect(() => () => espera.current?.cancelar(), []);

  function arrancarDescarga(): void {
    espera.current?.cancelar();
    setDescargando(true);
    espera.current = esperarDescarga(tokenDescarga, () => {
      setDescargando(false);
      // Token nuevo para la próxima planilla: el acuse es de un solo uso.
      setTokenDescarga(tokenDeDescarga());
      espera.current = null;
    });
  }

  function dejarDeEsperar(): void {
    espera.current?.cancelar();
    espera.current = null;
    setDescargando(false);
    setTokenDescarga(tokenDeDescarga());
  }

  /**
   * Mueve el período un mes. El intervalo de los eventuales lo sigue mientras
   * nadie lo haya tocado: tener que reescribir las dos fechas en cada cambio
   * de mes es el error que termina liquidando la ventana del mes anterior.
   */
  function irAlMes(pasos: number): void {
    const proximo = moverMes(anio, mes, pasos);
    setAnio(proximo.anio);
    setMes(proximo.mes);
    if (intervaloTocado) return;
    const limites = limitesDelMes(proximo.anio, proximo.mes);
    setDesde(limites.primerDia);
    setHasta(limites.ultimoDia);
  }

  const cambiarDesde = (valor: string): void => {
    setIntervaloTocado(true);
    setDesde(valor);
  };
  const cambiarHasta = (valor: string): void => {
    setIntervaloTocado(true);
    setHasta(valor);
  };

  function parametrosDeImpacto():
    | (SeleccionPeriodo & {
        operacion: string;
        conceptos: readonly string[];
        rangoDesde?: string;
    rangoHasta?: string;
      })
    | null {
    if (!consulta) return null;
    return {
      // De lo CONSULTADO, no de los controles: se impacta lo que está en
      // pantalla, que puede ser de otro período o de otros choferes.
      ...seleccionConsultada(consulta),
      operacion: consulta.operacion,
      // Solo los que de verdad se pueden mandar: un manual sin fecha está
      // tildado en pantalla pero no trajo nada.
      conceptos: [...elegidos].filter((c) => tildable(c)),
    };
  }

  /**
   * Abre el modal y corre el simulacro contra el servidor.
   *
   * El modal se abre igual aunque el simulacro falle: deja revisar y
   * seleccionar, y dice por qué no se puede mandar. Lo que no hace nunca es
   * ofrecer el botón de impactar sin haber verificado.
   */
  async function abrirImpacto(): Promise<void> {
    const parametros = parametrosDeImpacto();
    if (!parametros) return;

    setDialogoAbierto(true);
    setSimulacro(null);
    setIntento(null);
    setErrorImpacto(null);
    setPreparando(true);
    try {
      const r = await simularImpacto(parametros);
      setSimulacro(r);
      if (r.yaImpactado) {
        /*
         * Que el período tenga una corrida anterior no impide cargar lo que
         * quedó afuera: lo que no se puede repetir es una novedad, y esas ya
         * vienen descontadas del simulacro. Se avisa, no se bloquea.
         */
        setAvisos([
          mensaje(
            'info',
            `Este período ya tuvo una carga: la hizo ${r.yaImpactado.usuario} el ` +
              `${r.yaImpactado.impactadoEn}, con ${String(r.yaImpactado.aceptadas)} novedad(es) ` +
              'aceptada(s). Lo que ya está cargado no vuelve a mandarse.',
          ),
        ]);
      }
    } catch (err) {
      setErrorImpacto(mensajeDe(err));
    } finally {
      setPreparando(false);
    }
  }

  /**
   * Vuelve a preguntarle al servidor qué queda por cargar.
   *
   * Se llama después de cada intento: lo que entró ya no tiene que seguir en
   * la lista, y lo que Visma rechazó sí, porque se puede corregir y volver a
   * mandar. Sin esto habría que cerrar el modal y abrirlo de nuevo para ver
   * la realidad.
   */
  async function refrescarSimulacro(): Promise<void> {
    const parametros = parametrosDeImpacto();
    if (!parametros) return;
    setPreparando(true);
    try {
      setSimulacro(await simularImpacto(parametros));
    } catch (err) {
      setErrorImpacto(mensajeDe(err));
    } finally {
      setPreparando(false);
    }
  }

  async function impactarSeleccion(
    claves: ReadonlySet<string>,
    dniManual: ReadonlyMap<string, string>,
  ): Promise<void> {
    const parametros = parametrosDeImpacto();
    if (!parametros) return;

    setImpactando(true);
    setErrorImpacto(null);
    try {
      // Van los pares elegidos, nunca los importes: cuánto se liquida lo
      // vuelve a calcular el servidor contra el TMS.
      const items = [...claves].map((clave) => {
        const [dni = '', concepto = ''] = clave.split('|');
        const dniVisma = dniManual.get(dni);
        // Solo viaja cuando de verdad se cambió: el back rechaza el campo si
        // el portal no permite editarlo. Un campo a medio escribir no llega
        // acá: el diálogo no deja confirmar con un DNI inválido.
        return dniVisma === undefined || dniVisma === ''
          ? { dni, concepto }
          : { dni, concepto, dniVisma };
      });

      const r = await impactarEnVisma({ ...parametros, novedades: items });
      const rechazos = r.rechazadas ?? [];
      const aceptadas = r.aceptadas ?? 0;

      setIntento({
        tono: rechazos.length === 0 ? 'ok' : 'atencion',
        texto:
          rechazos.length === 0
            ? `Entraron ${String(aceptadas)} novedad(es) en la liquidación de ${mesYAnio(r.periodo)}.`
            : `Entraron ${String(aceptadas)} de ${String(items.length)} novedad(es) en ` +
              `${mesYAnio(r.periodo)}. ${String(rechazos.length)} las rechazó Visma.`,
        rechazadas: rechazos,
      });
      setAvisos([
        mensaje(
          rechazos.length === 0 ? 'ok' : 'atencion',
          `${r.operacion.toUpperCase()} ${mesYAnio(r.periodo)}: se impactaron ${String(aceptadas)} ` +
            'novedad(es) en Visma.',
        ),
      ]);
    } catch (err) {
      /*
       * El modal queda abierto y la selección se puede rehacer: un error acá
       * suele ser "esto ya estaba cargado", y lo que sigue es elegir lo que
       * falta, no cerrar y volver a entrar.
       */
      setIntento({ tono: 'error', texto: mensajeDe(err), rechazadas: [] });
    } finally {
      setImpactando(false);
    }

    // Pase lo que pase, la lista se pone al día con lo que el servidor sabe.
    await refrescarSimulacro();
  }

  async function enviar(ev: React.FormEvent): Promise<void> {
    ev.preventDefault();
    await consultarAhora();
  }

  /**
   * La consulta, una sola: la piden el botón de la banda y el del bloque de
   * conceptos manuales. Dos caminos distintos terminarían mostrando dos fotos
   * del mismo período que no coinciden.
   */
  async function consultarAhora(): Promise<void> {
    if (operacion === '' || seleccion === null) return;

    setConsultando(true);
    setAvisos([]);
    // Una consulta nueva vuelve al principio de las tablas.
    setPaginaNovedades(1);
    setPaginaTramos(1);
    try {
      const datos = await consultar({ ...seleccion, operacion });
      setConsulta(datos);
      setHoraConsulta(
        new Date().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }),
      );
      /*
       * Todo tildado, menos los de fecha manual cuando la consulta no trajo
       * fecha: esos no tienen datos, y dejarlos tildados en cero haría creer
       * que no hay nada que liquidar.
       */
      const hayFechaManual = datos.rangoManual !== null;
      setElegidos(
        new Set(
          datos.conceptos.filter((c) => !c.rangoPropio || hayFechaManual).map((c) => c.concepto),
        ),
      );

      const nuevos = datos.advertencias.map((a) => mensaje('atencion', a));
      const sinFecha = datos.conceptos.filter((c) => c.rangoPropio && !hayFechaManual);
      if (sinFecha.length > 0) {
        nuevos.push(
          mensaje(
            'atencion',
            `${enumerar(sinFecha.map((c) => c.concepto))} ${sinFecha.length === 1 ? 'necesita' : 'necesitan'} ` +
              'fecha manual: cargala debajo del listado de conceptos y consultá. Hasta ' +
              'entonces no ' +
              'entran en la previsualización ni en el impacto.',
          ),
        );
      }
      if (datos.tramos.length === 0) {
        const cuando =
          datos.intervalo === null
            ? periodoLegible(anio, mes)
            : `${textoDeRango(datos.intervalo)}`;
        nuevos.push(
          mensaje(
            'atencion',
            `No hay tramos liquidables de ${operacion} en ${cuando} para ` +
              `${nombreDeLosTipos(datos.tiposChofer)}.`,
          ),
        );
      }
      setAvisos(nuevos);
    } catch (err) {
      setConsulta(null);
      setAvisos([mensaje('error', mensajeDe(err))]);
    } finally {
      setConsultando(false);
    }
  }

  const tramos = consulta?.tramos ?? [];
  const conceptos = consulta?.conceptos ?? [];

  /*
   * Los conceptos de **fecha manual** van aparte: no se liquidan por el mes y
   * sin su fecha no traen datos. La tabla principal lista el resto.
   */
  const manuales = conceptosManuales(conceptos);
  const delMes = conceptosDelMes(conceptos);
  /** La consulta que está en pantalla trajo fecha para los manuales. */
  const conFechaManual = consulta?.rangoManual != null;
  const tildable = (codigo: string): boolean => {
    const c = conceptos.find((x) => x.concepto === codigo);
    return c === undefined || sePuedeTildar(c, conFechaManual);
  };
  const rangoConsultado =
    consulta?.rangoManual == null ? 'sin fecha' : textoDeRango(consulta.rangoManual);
  const conceptosManualesPorCodigo = new Set(manuales.map((c) => c.concepto));

  /*
   * Lo que se previsualiza y se impacta. Un concepto de fecha manual sin fecha
   * consultada queda afuera aunque esté tildado: no trajo datos, y mandarlo lo
   * único que logra es que el servidor lo trabe.
   *
   * Las dos tablas se recortan acá y no en el servidor: la consulta ya trajo
   * el período entero, y pedirlo de nuevo por página volvería a golpear el
   * TMS para mostrar lo que el navegador ya tiene.
   */
  const novedades = (consulta?.novedades ?? []).filter(
    (n) => elegidos.has(n.concepto) && tildable(n.concepto),
  );

  const pConceptos = paginaValida(paginaConceptos, delMes.length, POR_PAGINA);
  const conceptosVisibles = delMes.slice((pConceptos - 1) * POR_PAGINA, pConceptos * POR_PAGINA);

  const pNovedades = paginaValida(paginaNovedades, novedades.length, POR_PAGINA_NOVEDADES);
  const novedadesVisibles = novedades.slice(
    (pNovedades - 1) * POR_PAGINA_NOVEDADES,
    pNovedades * POR_PAGINA_NOVEDADES,
  );

  /*
   * Cómo se llama cada concepto, para no repetir el código pelado en la
   * previsualización: "01178" solo no le dice nada a quien revisa.
   */
  const descripcionDe = new Map(conceptos.map((c) => [c.concepto, c.descripcion]));

  const pTramos = paginaValida(paginaTramos, tramos.length, POR_PAGINA);
  const tramosVisibles = tramos.slice((pTramos - 1) * POR_PAGINA, pTramos * POR_PAGINA);

  const alternar = (concepto: string): void => {
    // Un concepto sin fecha consultada no se puede tildar: la casilla está
    // apagada, pero la fila entera también alterna y por ahí sí llega.
    if (!tildable(concepto)) return;
    setElegidos((actuales) => {
      const proximos = new Set(actuales);
      if (!proximos.delete(concepto)) proximos.add(concepto);
      return proximos;
    });
    // La previsualización se arma con lo tildado: si cambia, se vuelve al
    // principio en lugar de dejar al operador en una página que ya no existe.
    setPaginaNovedades(1);
  };

  /*
   * Estado de los dos rangos de la banda. El de los eventuales traba la
   * consulta; el de los manuales no, porque es opcional.
   */
  const problemaIntervalo = problemaDelRango(desde, hasta);
  const periodoDelIntervalo = problemaIntervalo === null ? hasta.slice(0, 7) : '';
  const problemaManual =
    manualDesde === '' && manualHasta === '' ? null : problemaDelRango(manualDesde, manualHasta);
  const manualEnviable = manualDesde !== '' && manualHasta !== '' && problemaManual === null;
  const estadoManual = problemaManual !== null ? 'error' : manualEnviable ? 'ok' : 'vacio';
  /*
   * Qué dice la marca del campo. Antes de la primera consulta no se sabe
   * cuántos conceptos manuales hay —los trae la consulta—, así que no se
   * inventa un número.
   */
  const marcaManual =
    problemaManual ??
    (consulta === null
      ? 'opcional'
      : manuales.length === 0
        ? 'sin conceptos manuales'
        : manualEnviable
          ? `${String(manuales.length)} concepto(s)`
          : 'opcional · sin fecha');

  /*
   * ------------------------------------------------------------------ banda
   */
  const seleccion = seleccionDe(
    tipos,
    { anio, mes },
    { desde, hasta },
    { desde: manualDesde, hasta: manualHasta },
  );
  const desactualizado = estaDesactualizado(consulta, {
    anio,
    mes,
    tipos,
    intervalo: tipos.has('EVENTUAL') ? { desde, hasta } : { desde: '', hasta: '' },
    manual: manualEnviable ? { desde: manualDesde, hasta: manualHasta } : { desde: '', hasta: '' },
  });

  const sello = selloDe({
    hayOperaciones: disponibles.length > 0,
    consulta,
    desactualizado,
    hora: horaConsulta,
    periodoLegible: (c) =>
      `${c.operacion.toUpperCase()} · ${nombreDeLosTipos(c.tiposChofer)} · ${mesYAnio(c.periodo)}` +
      (textoDeLoConsultado(c) === '' ? '' : ` (${textoDeLoConsultado(c)})`),
  });

  const todosElegidos =
    conceptos.length > 0 &&
    conceptos.filter((c) => tildable(c.concepto)).every((c) => elegidos.has(c.concepto));

  /* Cuántos de los colaboradores previsualizados son eventuales. */
  const eventuales = new Set(
    novedades.filter((n) => n.tipoChofer === 'Eventual').map((n) => n.dni),
  ).size;

  /*
   * Lo que el modal lista: lo que el servidor dice que se mandaría, no lo que
   * hay en pantalla. Después de cargar una parte, el simulacro vuelve sin las
   * que ya entraron, y el modal tiene que mostrar eso.
   *
   * Si el simulacro no llegó —sesión vencida, base caída— se cae a la lista de
   * la pantalla: así se puede revisar igual, aunque no se pueda mandar.
   */
  const novedadesDelModal = useMemo(
    () =>
      simulacro?.items === undefined
        ? novedades
        : simulacro.items.map((i) => ({
            dni: i.dni,
            nombre: i.nombre,
            legajo: i.legajo,
            tipoChofer: i.tipoChofer,
            concepto: i.concepto,
            valor: i.valor,
            unidad: i.unidad,
            fechaDesde: i.desde,
            fechaHasta: i.hasta,
          })),
    [simulacro, novedades],
  );

  /*
   * Un solo paso activo a la vez. Con la consulta hecha y conceptos tildados,
   * el paso en curso es la previsualización: el de conceptos queda cumplido.
   */
  const pasoConceptos: EstadoPaso = !consulta
    ? 'pendiente'
    : novedades.length > 0
      ? 'hecho'
      : 'activo';
  const pasoPrevia: EstadoPaso = novedades.length > 0 ? 'activo' : 'pendiente';

  return (
    <div className="novedades">
      <header className="novedades-encabezado">
        <div>
          <h1>Novedades TMS → Visma</h1>
          <p className="bajada">
            Se consulta el TMS, se revisa lo que se liquidaría y se impacta en Visma Payroll.
          </p>
        </div>

        <ol className="pasos">
          <Paso indice={1} nombre="Consulta" estado={consulta ? 'hecho' : 'activo'} />
          <li className="union" aria-hidden="true" />
          <Paso indice={2} nombre="Conceptos" estado={pasoConceptos} />
          <li className="union" aria-hidden="true" />
          <Paso indice={3} nombre="Previsualización" estado={pasoPrevia} />
        </ol>
      </header>

      <form className="banda banda--filtros" onSubmit={(ev) => void enviar(ev)}>
        <label className="campo">
          Operación
          <select
            value={operacion}
            required
            onChange={(ev) => {
              setOperacion(ev.target.value);
              // Otra operación es otro padrón: lo que hay en pantalla no sirve.
              setConsulta(null);
            }}
          >
            {disponibles.length === 0 && <option value="">Sin operaciones configuradas</option>}
            {disponibles.map((nombre) => (
              <option key={nombre} value={nombre}>
                {nombre.toUpperCase()}
              </option>
            ))}
          </select>
        </label>

        {/*
          El período se mueve de a un mes. Un desplegable de doce meses más un
          campo de año obligan a dos gestos para lo que se hace siempre, que es
          ir al mes anterior.
        */}
        <div className="campo">
          <span className="campo-etiqueta">
            Período de liquidación
            {esMesVencido(anio, mes) && <span className="marca marca--info">mes vencido</span>}
          </span>
          <span className="paso-mes">
            <button
              type="button"
              aria-label="Mes anterior"
              onClick={() => irAlMes(-1)}
            >
              <ChevronLeft size={16} aria-hidden="true" />
            </button>
            <span className="paso-mes-valor" aria-live="polite">
              {`${MESES[mes - 1] ?? ''} ${String(anio)}`}
            </span>
            <button type="button" aria-label="Mes siguiente" onClick={() => irAlMes(1)}>
              <ChevronRight size={16} aria-hidden="true" />
            </button>
          </span>
        </div>

        {/*
          De qué choferes. Nunca queda vacío: sin choferes no hay consulta, así
          que destildar el último no hace nada.
        */}
        <div className="campo" role="group" aria-label="Choferes">
          <span className="campo-etiqueta">Choferes</span>
          <span className="segmentos">
            {ORDEN_CHOFERES.map((t) => {
              const activo = tipos.has(t);
              return (
                <button
                  key={t}
                  type="button"
                  role="checkbox"
                  aria-checked={activo}
                  data-on={activo ? 'si' : 'no'}
                  onClick={() => {
                    setTipos((actuales) => alternarChofer(actuales, t));
                  }}
                >
                  {activo && <Check size={12} aria-hidden="true" />}
                  {t === 'PROPIO' ? 'Propios' : 'Eventuales'}
                </button>
              );
            })}
          </span>
        </div>

        {/*
          El intervalo de los eventuales: su ciclo no es el mes calendario, así
          que la ventana se escribe. Solo aparece si se los consulta.
        */}
        {tipos.has('EVENTUAL') && (
          <div className="campo campo--doble" role="group" aria-label="Intervalo de los eventuales">
            <span className="campo-etiqueta">
              Intervalo de los eventuales
              <span className="marca marca--atencion">
                {problemaIntervalo ?? `liquida en ${mesYAnio(periodoDelIntervalo)}`}
              </span>
            </span>
            <span
              className="campo-rango"
              data-estado={problemaIntervalo === null ? 'ok' : 'error'}
            >
              <CampoFecha etiqueta="Desde" value={desde} onChange={cambiarDesde} />
              <CampoFecha etiqueta="Hasta" value={hasta} onChange={cambiarHasta} />
            </span>
          </div>
        )}

        <button type="submit" disabled={consultando || operacion === '' || seleccion === null}>
          {consultando ? (
            <Loader2 className="girando" size={16} aria-hidden="true" />
          ) : (
            <Search size={16} aria-hidden="true" />
          )}
          {consultando ? 'Consultando…' : desactualizado ? 'Consultar ahora' : 'Consultar'}
        </button>

        {/* De qué son los números que están en pantalla. Es lo único que
            separa "esto es lo que voy a impactar" de "esto es de otra
            consulta". */}
        <span className="sello-consulta" data-tono={sello.tono}>
          {sello.texto}
        </span>
      </form>

      <Avisos mensajes={avisos} />

      {consulta && (
        <div className="novedades-grilla">
          <section className="tarjeta tarjeta--tabla">
            <div className="tarjeta-titulo">
              <h2>Conceptos a impactar</h2>
              {/* Cuenta normales y manuales juntos: es lo que se va a cargar. */}
              <span className="nota">
                {`${String(elegidos.size)} de ${String(conceptos.length)} tildados`}
              </span>
            </div>

            {conceptos.length === 0 ? (
              <p className="tabla-vacia">
                La consulta no trajo ningún concepto con datos en el período.
              </p>
            ) : (
              <EnvoltorioTabla
                pie={
                  <Paginacion
                    total={delMes.length}
                    pagina={pConceptos}
                    tamanio={POR_PAGINA}
                    onCambiar={setPaginaConceptos}
                    etiqueta="conceptos"
                  />
                }
              >
                <table className="tabla tabla--conceptos">
                  <thead>
                    <tr>
                      <th scope="col" className="col-check">
                        <input
                          type="checkbox"
                          checked={todosElegidos}
                          aria-label="Impactar todos los conceptos"
                          onChange={(ev) =>
                            setElegidos(
                              ev.target.checked
                                ? // Solo los que se pueden tildar: un manual sin
                                  // fecha consultada no trae datos.
                                  new Set(
                                    conceptos
                                      .filter((c) => tildable(c.concepto))
                                      .map((c) => c.concepto),
                                  )
                                : new Set(),
                            )
                          }
                        />
                      </th>
                      <th scope="col" className="num">Concepto</th>
                      <th scope="col">Qué acumula</th>
                      <th scope="col" className="num">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {conceptosVisibles.map((c) => {
                      const elegido = elegidos.has(c.concepto);
                      return (
                        <tr
                          key={c.concepto}
                          data-elegido={elegido ? 'si' : 'no'}
                          onClick={() => alternar(c.concepto)}
                        >
                          <td className="col-check">
                            <input
                              type="checkbox"
                              checked={elegido}
                              aria-label={`Impactar el concepto ${c.concepto}`}
                              // La fila entera alterna: sin esto el clic en la
                              // casilla llega dos veces y se cancela a sí mismo.
                              onClick={(ev) => ev.stopPropagation()}
                              onChange={() => alternar(c.concepto)}
                            />
                          </td>
                          <td className="num codigo">{c.concepto}</td>
                          <td>
                            {c.descripcion}
                            <span className="sub">{`${String(c.colaboradores)} colaboradores`}</span>
                          </td>
                          <td className="num">
                            <strong>{numero(c.total)}</strong>
                            {c.unidad !== '' && <span className="unidad">{c.unidad}</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </EnvoltorioTabla>
            )}

            {manuales.length > 0 && (
              /*
                Los conceptos que no se liquidan por el mes. Van abajo y no
                mezclados en la tabla: sin fecha no tienen total ni
                colaboradores, y una fila en cero entre las demás se lee como
                "no hay nada que pagar".
              */
              <div className="conceptos-manuales">
                <div className="conceptos-manuales-titulo">
                  <h3>
                    Conceptos con fecha manual
                    <span className="marca marca--atencion">{marcaManual}</span>
                  </h3>
                  <span className="sub">
                    {`No se liquidan por el mes: usan la fecha de acá abajo. En pantalla: ${rangoConsultado}.`}
                  </span>
                </div>

                {manuales.map((c) => {
                  const tildado = elegidos.has(c.concepto) && conFechaManual;
                  return (
                    <div
                      key={c.concepto}
                      className="concepto-manual"
                      data-estado={conFechaManual ? 'ok' : 'pendiente'}
                    >
                      <input
                        type="checkbox"
                        checked={tildado}
                        disabled={!conFechaManual}
                        aria-label={`Impactar el concepto ${c.concepto}`}
                        title={
                          conFechaManual
                            ? `Impactar ${c.concepto}`
                            : 'Cargá Desde y Hasta debajo del listado y consultá'
                        }
                        onChange={() => alternar(c.concepto)}
                      />
                      <span className="num codigo">{c.concepto}</span>
                      <span>
                        {c.descripcion}
                        <span className="sub">
                          {conFechaManual
                            ? `${String(c.colaboradores)} colaboradores · ${numero(c.total)} ${c.unidad}`
                            : 'Sin consultar'}
                        </span>
                      </span>
                      <span className="estado">
                        {conFechaManual
                          ? tildado
                            ? `Entra con ${rangoConsultado}`
                            : 'Con fecha · destildado'
                          : 'Sin fecha cargada: no se puede tildar'}
                      </span>
                    </div>
                  );
                })}

                {/*
                  Las fechas van debajo del listado y no en la banda: son de
                  estos conceptos y de ninguno más, y acá se las lee al lado de
                  lo que cambian.

                  El botón vuelve a traer el período entero, que es la única
                  consulta que hay: así la pantalla queda con una sola foto y
                  no con dos pegadas que podrían no ser del mismo momento.
                */}
                <div className="conceptos-manuales-fecha" data-estado={estadoManual}>
                  <CampoFecha etiqueta="Desde" value={manualDesde} onChange={setManualDesde} />
                  <CampoFecha etiqueta="Hasta" value={manualHasta} onChange={setManualHasta} />
                  <button
                    type="button"
                    className={desactualizado ? 'primaria' : 'plano'}
                    disabled={consultando || problemaManual !== null}
                    onClick={() => void consultarAhora()}
                  >
                    {consultando ? (
                      <Loader2 className="girando" size={16} aria-hidden="true" />
                    ) : (
                      <Search size={16} aria-hidden="true" />
                    )}
                    {consultando ? 'Consultando...' : 'Consultar con estas fechas'}
                  </button>
                  {/* Por qué el botón está apagado: si no, parece trabado. */}
                  {problemaManual !== null && (
                    <span className="conceptos-manuales-problema">{problemaManual}</span>
                  )}
                </div>
              </div>
            )}
          </section>

          <section className="tarjeta tarjeta--tabla">
            <div className="tarjeta-titulo">
              <h2>Previsualización</h2>

              {/* Las dos acciones van en un grupo propio: sueltas, el
                  `space-between` del título las separa uno de cada lado. */}
              <div className="acciones">
                {consulta && (
                  /*
                    Un enlace de verdad, no un botón con JavaScript: la descarga
                    la resuelve el navegador contra el servidor. Apunta al
                    período CONSULTADO, no al de los selectores, para que la
                    planilla sea siempre la de los números que están en
                    pantalla.
                  */
                  <a
                    className="boton plano"
                    href={urlConsultaXlsx(
                      {
                        ...seleccionConsultada(consulta),
                        operacion: consulta.operacion,
                        // El rango consultado también: la planilla tiene que
                        // traer los mismos números que están en pantalla.
                      },
                      tokenDescarga,
                    )}
                    download
                    onClick={arrancarDescarga}
                  >
                    <Download size={16} aria-hidden="true" />
                    Descargar en Excel
                  </a>
                )}

                {puedeImpactar ? (
                  /* Solo lo ven Administrador y Liquidador. El servidor vuelve
                     a exigir el rol: esto decide qué se dibuja, no quién
                     puede. */
                  <button
                    type="button"
                    className="primaria"
                    disabled={novedades.length === 0}
                    onClick={() => void abrirImpacto()}
                  >
                    <Send size={16} aria-hidden="true" />
                    Impactar en Visma
                  </button>
                ) : (
                  <span className="nota">{`${String(novedades.length)} novedad(es)`}</span>
                )}
              </div>
            </div>

            {novedades.length === 0 ? (
              <p className="tabla-vacia">
                {conceptos.length > 0
                  ? 'No quedó ningún concepto tildado. Tildá al menos uno para ver lo que se liquidaría.'
                  : 'No hay novedades para previsualizar en este período.'}
              </p>
            ) : (
              <>
                <div className="resumen">
                  <Dato valor={novedades.length} etiqueta="Novedades" />
                  <Dato
                    valor={new Set(novedades.map((n) => n.dni)).size}
                    etiqueta="Colaboradores"
                    /* El desglose va debajo del número y no como dato aparte:
                       es la misma cifra partida en dos, no otra medición. */
                    detalle={
                      eventuales === 0
                        ? ''
                        : `${String(new Set(novedades.map((n) => n.dni)).size - eventuales)} propios · ${String(eventuales)} eventuales`
                    }
                  />
                  <Dato valor={tramos.length} etiqueta="Tramos liquidables" />
                </div>

                <EnvoltorioTabla
                  pie={
                    <Paginacion
                      total={novedades.length}
                      pagina={pNovedades}
                      tamanio={POR_PAGINA_NOVEDADES}
                      onCambiar={setPaginaNovedades}
                      etiqueta="novedades"
                    />
                  }
                >
                  <table className="tabla">
                    <thead>
                      <tr>
                        <th scope="col" className="num">DNI</th>
                        <th scope="col">Colaborador</th>
                        <th scope="col">Concepto</th>
                        <th scope="col" className="num">Valor</th>
                        <th scope="col" className="num">Período</th>
                      </tr>
                    </thead>
                    <tbody>
                      {novedadesVisibles.map((n) => (
                        <tr key={`${n.dni}|${n.concepto}`}>
                          <td className="num">{n.dni}</td>
                          <td>
                            <span className="nombre">{n.nombre}</span>
                            <span className="legajo">{`leg. ${n.legajo}`}</span>
                            <ChipEventual tipo={n.tipoChofer} />
                          </td>
                          <td>
                            <span className="codigo">{n.concepto}</span>
                            <span className="sub">{descripcionDe.get(n.concepto) ?? ''}</span>
                          </td>
                          {/* Sin unidad: acá cada fila es un concepto solo, y la
                              unidad ya está en la tabla de la izquierda. */}
                          <td className="num">
                            <strong>{numero(n.valor)}</strong>
                          </td>
                          {/* El período de liquidación —mes y año—, que es lo
                              que se informa a Visma. Las fechas de los tramos
                              que originaron la novedad están en el detalle de
                              tramos y en el Excel. */}
                          {/* El concepto de fecha manual no se liquida por el
                              mes: su período son las fechas que se cargaron. */}
                          {conceptosManualesPorCodigo.has(n.concepto) &&
                          consulta.rangoManual !== null ? (
                            <td className="num periodo-manual">
                              {`${textoDeRango(consulta.rangoManual)} · manual`}
                            </td>
                          ) : (
                            <td className="num">{mesYAnio(consulta.periodo)}</td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </EnvoltorioTabla>
              </>
            )}
          </section>
        </div>
      )}

      {/*
        La planilla se arma volviendo a consultar el TMS: un mes grande tarda, y
        sin cartel la pantalla se queda quieta sin decir que está trabajando.
        Se cierra cuando el servidor acusa que el archivo salió, no a los N
        segundos.
      */}
      {consulta && (
        <DialogoImpactar
          abierto={dialogoAbierto}
          operacion={consulta.operacion}
          periodo={consulta.periodo}
          novedades={novedadesDelModal}
          conceptos={consulta.conceptos}
          bloqueo={errorImpacto}
          trabadas={simulacro?.trabadas ?? []}
          intento={intento}
          preparando={preparando}
          enviando={impactando}
          dniEditable={simulacro?.dniEditable === true}
          onCerrar={() => setDialogoAbierto(false)}
          onImpactar={(claves, dniManual) => void impactarSeleccion(claves, dniManual)}
        />
      )}

      <ModalCargando
        abierto={descargando}
        titulo="Descargando informe"
        detalle="Se vuelve a consultar el TMS y se arma la planilla del período."
        notaDemora="Está tardando más de lo habitual. Si el archivo ya se descargó, se puede cerrar este cartel."
        onCancelar={dejarDeEsperar}
      />

      {tramos.length > 0 && (
        /* `details` nativo y no estado de React: abre con teclado y con lector
           de pantalla sin una línea más de código. */
        <details
          className="plegable"
          onToggle={(ev) => setTramosAbiertos(ev.currentTarget.open)}
        >
          <summary>
            <span className="chevron" aria-hidden="true">
              <ChevronRight size={14} />
            </span>
            <span>
              <strong>Detalle de tramos del TMS</strong>
              <span className="sub">
                {`${tramos.length.toLocaleString('es-AR')} tramos de ${consulta?.periodo ?? ''}, para auditar de dónde sale cada número.`}
              </span>
            </span>
            <span className="accion">{tramosAbiertos ? 'Ocultar' : 'Mostrar'}</span>
          </summary>

          <EnvoltorioTabla
            pie={
              <Paginacion
                total={tramos.length}
                pagina={pTramos}
                tamanio={POR_PAGINA}
                onCambiar={setPaginaTramos}
                etiqueta="tramos"
              />
            }
          >
            <table className="tabla">
              <thead>
                <tr>
                  <th scope="col" className="num">DNI</th>
                  <th scope="col">Colaborador</th>
                  <th scope="col">Chofer</th>
                  <th scope="col">Función</th>
                  <th scope="col" className="num">Viaje</th>
                  <th scope="col" className="num">Inicio</th>
                  <th scope="col">Servicio</th>
                  <th scope="col" className="num">Cuenta</th>
                  <th scope="col" className="num">Km</th>
                  <th scope="col" className="num">Normal</th>
                  <th scope="col" className="num">100%</th>
                  <th scope="col" className="num">Pal. entr.</th>
                  <th scope="col">Adicionales</th>
                </tr>
              </thead>
              <tbody>
                {tramosVisibles.map((t) => (
                  <tr key={clave(t)}>
                    <td className="num">{t.dni}</td>
                    <td>{t.nombreChofer}</td>
                    <td>
                      <span className="chip" data-eventual={t.tipoChofer === 'Eventual' ? 'si' : 'no'}>
                        {t.tipoChofer}
                      </span>
                    </td>
                    {/* Chip, como en Jornadas: etiqueta un valor, no es un estado. */}
                    <td><span className="chip">{t.funcion}</span></td>
                    <td className="num">{`${t.nroViaje}/${String(t.tramo)}`}</td>
                    <td className="num">{soloFecha(t.fechaInicio)}</td>
                    <td>{`${t.codigoServicio} · ${t.servicio}`}</td>
                    <td className="num">{`${t.cuenta}-${t.sub}`}</td>
                    <td className="num">{numero(t.kmTotal)}</td>
                    <td className="num">{numero(t.horasNormal)}</td>
                    <td className="num">{numero(t.horas100)}</td>
                    <td className="num">{numero(t.palletsEntregados)}</td>
                    <td>{t.adicionales.join(' · ') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </EnvoltorioTabla>
        </details>
      )}
    </div>
  );
}

/**
 * Marca al chofer eventual al lado de su nombre.
 *
 * Solo al eventual: con las dos poblaciones mezcladas lo que hay que poder
 * ver de un vistazo es quién NO es de la casa. Etiquetar también al propio
 * agrega una marca en casi todas las filas y deja de leerse ninguna.
 */
function ChipEventual({ tipo }: { readonly tipo: string }): React.JSX.Element | null {
  if (tipo !== 'Eventual') return null;
  return (
    <span className="chip chip--eventual" title="Chofer de una empresa ajena al grupo">
      Eventual
    </span>
  );
}

/** Un tramo se identifica por viaje + orden + colaborador: puede ir con acompañante. */
const clave = (t: TramoTms): string => `${t.nroViaje}|${String(t.tramo)}|${t.dni}|${t.funcion}`;

/** Un paso del flujo: el número, el nombre y en cuál se está. */
function Paso({
  indice,
  nombre,
  estado,
}: {
  readonly indice: number;
  readonly nombre: string;
  readonly estado: EstadoPaso;
}): React.JSX.Element {
  return (
    <li className="paso" data-estado={estado}>
      <span className="indice">
        {estado === 'hecho' ? <Check size={12} aria-hidden="true" /> : indice}
      </span>
      {nombre}
    </li>
  );
}

function Dato({
  valor,
  etiqueta,
  detalle = '',
}: {
  readonly valor: string | number;
  readonly etiqueta: string;
  /** El mismo número partido en dos, debajo. Vacío para no mostrar nada. */
  readonly detalle?: string;
}): React.JSX.Element {
  return (
    <div className="dato">
      <span className="etiqueta">{etiqueta}</span>
      <strong>{valor}</strong>
      {detalle !== '' && <span className="sub">{detalle}</span>}
    </div>
  );
}
